import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _ProseMirror;
import { css } from '@emotion/react';
import { decisionListSelector, taskListSelector } from '@atlaskit/adf-schema';
import { tableFullPageEditorStyles } from '@atlaskit/editor-plugins/table/ui/common-styles';
import { tableMarginFullWidthMode } from '@atlaskit/editor-plugins/table/ui/consts';
import { akEditorContextPanelWidth, akEditorFullWidthLayoutWidth, akEditorGutterPadding, akEditorSwoopCubicBezier, akLayoutGutterOffset, ATLASSIAN_NAVIGATION_HEIGHT } from '@atlaskit/editor-shared-styles';
import { createEditorContentStyle } from '../../ContentStyles';
import { scrollbarStyles } from '../../styles';
var SWOOP_ANIMATION = "0.5s ".concat(akEditorSwoopCubicBezier);
var TOTAL_PADDING = akEditorGutterPadding * 2;
export var fullPageEditorWrapper = css({
  minWidth: '340px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box'
});
var scrollStyles = css({
  flexGrow: 1,
  height: '100%',
  overflowY: 'scroll',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  scrollBehavior: 'smooth'
}, scrollbarStyles);
export var ScrollContainer = createEditorContentStyle(scrollStyles);
ScrollContainer.displayName = 'ScrollContainer';

// transition used to match scrollbar with config panel opening animation
// only use animation when opening as there is a bug with floating toolbars.
export var positionedOverEditorStyle = css({
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
  paddingRight: "".concat(akEditorContextPanelWidth, "px"),
  transition: "padding 500ms ".concat(akEditorSwoopCubicBezier),
  '.fabric-editor-popup-scroll-parent': {
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
    paddingLeft: "".concat(akEditorContextPanelWidth, "px"),
    transition: "padding 500ms ".concat(akEditorSwoopCubicBezier)
  }
});
export var contentArea = css({
  display: 'flex',
  flexDirection: 'row',
  height: "calc(100% - ".concat(ATLASSIAN_NAVIGATION_HEIGHT, ")"),
  boxSizing: 'border-box',
  margin: 0,
  padding: 0,
  transition: "padding 0ms ".concat(akEditorSwoopCubicBezier)
});
export var contentAreaHeightNoToolbar = css({
  height: '100%'
});
export var sidebarArea = css({
  height: '100%',
  boxSizing: 'border-box',
  alignSelf: 'flex-end'
});

// initially hide until we have a containerWidth and can properly size them,
// otherwise they can cause the editor width to extend which is non-recoverable
export var editorContentAreaHideContainer = css({
  '.fabric-editor--full-width-mode': {
    '.pm-table-container, .code-block, .extension-container': {
      display: 'none'
    },
    '.multiBodiedExtension--container': {
      display: 'none'
    }
  }
});

/* Prevent horizontal scroll on page in full width mode */
var editorContentAreaContainerStyle = function editorContentAreaContainerStyle(containerWidth) {
  return css({
    '.fabric-editor--full-width-mode': {
      '.pm-table-container, .code-block, .extension-container': {
        maxWidth: "".concat(containerWidth - TOTAL_PADDING - tableMarginFullWidthMode * 2, "px")
      },
      '.extension-container.inline': {
        maxWidth: '100%'
      },
      'td .extension-container.inline': {
        maxWidth: 'inherit'
      },
      '.multiBodiedExtension--container': {
        maxWidth: "".concat(containerWidth - TOTAL_PADDING - tableMarginFullWidthMode * 2, "px")
      },
      '[data-layout-section]': {
        maxWidth: "".concat(containerWidth - TOTAL_PADDING + akLayoutGutterOffset * 2, "px")
      }
    }
  });
};
export var editorContentAreaStyle = function editorContentAreaStyle(_ref) {
  var layoutMaxWidth = _ref.layoutMaxWidth,
    fullWidthMode = _ref.fullWidthMode,
    containerWidth = _ref.containerWidth;
  return [editorContentArea, !fullWidthMode && editorContentAreaWithLayoutWith(layoutMaxWidth), containerWidth ? editorContentAreaContainerStyle(containerWidth) : editorContentAreaHideContainer];
};
var editorContentAreaWithLayoutWith = function editorContentAreaWithLayoutWith(layoutMaxWidth) {
  return css({
    maxWidth: "".concat(layoutMaxWidth + TOTAL_PADDING, "px")
  });
};
var editorContentArea = css({
  lineHeight: '24px',
  paddingTop: "var(--ds-space-600, 48px)",
  paddingBottom: "var(--ds-space-600, 48px)",
  height: 'calc( 100% - 105px )',
  width: '100%',
  margin: 'auto',
  flexDirection: 'column',
  flexGrow: 1,
  maxWidth: "".concat(akEditorFullWidthLayoutWidth + TOTAL_PADDING, "px"),
  transition: "max-width ".concat(SWOOP_ANIMATION),
  '& .ProseMirror': (_ProseMirror = {
    flexGrow: 1,
    boxSizing: 'border-box',
    '& > *': {
      clear: 'both'
    }
  }, _defineProperty(_ProseMirror, "> p, > ul, > ol:not(".concat(taskListSelector, "):not(").concat(decisionListSelector, "), > h1, > h2, > h3, > h4, > h5, > h6"), {
    clear: 'none'
  }), _defineProperty(_ProseMirror, '> p:last-child', {
    marginBottom: "var(--ds-space-300, 24px)"
  }), _ProseMirror)
}, tableFullPageEditorStyles, {
  '.fabric-editor--full-width-mode': {
    '.fabric-editor-breakout-mark, .extension-container.block, .pm-table-container': {
      width: '100% !important'
    },
    '.fabric-editor-breakout-mark': {
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
      marginLeft: 'unset !important',
      transform: 'none !important'
    }
  }
});
export var editorContentGutterStyle = css({
  boxSizing: 'border-box',
  padding: "0 ".concat("var(--ds-space-400, 32px)")
});